import {
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { deleteObject, getDownloadURL, listAll, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from './firebase';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';
import {
  isStorageUnauthorizedError,
  purchaseAttachmentUploadErrorMessage,
  purchaseCallableErrorMessage,
  purchaseMarkPurchasedErrorMessage,
} from './purchaseAttachmentUploadError';
import {
  canCancelPurchaseRequisition,
  canDeletePurchaseRequisition,
  canEditPurchasedPurchaseAmounts,
  canManagePurchaseSupportingAttachments,
  canOverridePurchaseApproval,
  canRemovePurchaseReceipt,
  canReassignPurchaseRequester,
  canReviseIssuedPurchaseAmount,
  type PurchasingActorAccess,
} from './purchasingAccess';
import {
  approvedPurchaseAmountBaseline,
  issuedPurchaseAmountEditKind,
} from './purchaseApprovedAmount';
import { purchasedAmountEditKind } from './purchasedAmountEdit';
import type {
  ApprovalStepStatus,
  PurchaseAmountEditAuditEntry,
  PurchaseApprovalStep,
  PurchaseAttachment,
  PurchaseAttachmentKind,
  PurchaseCostObject,
  PurchaseHoldInternal,
  PurchaseLineItem,
  PurchaseLink,
  PurchaseQbBillStatus,
  PurchaseRequisition,
  PurchaseRequisitionStatus,
  PurchaseVarianceRequest,
  PurchasedSupplierOrder,
} from '../types/purchasing';
import { isPurchaseQbBillStatus } from './purchaseQbBillStatus';
import {
  everySupplierPoPurchased,
  findPurchasedSupplierOrder,
  issuedSupplierPurchaseOrders,
  purchasedSupplierOrders,
  sumPurchasedSupplierOrders,
  supplierPurchaseOrderByNumber,
} from './purchaseOrderPurchases';
import {
  currentPendingStep,
  currentPendingStepIndex,
  finalizePurchaseApprovalStep,
} from './purchasingApprovals';
import {
  parsePurchaseLineItems,
  requisitionTotal,
  serializePurchaseLineItems,
  summarizeRequisitionTitle,
} from './purchasingLineItems';
import {
  assignPoNumbersToLines,
  clearLinePoNumbers,
  groupLinesByVendor,
  purchaseOrderNumbers,
  vendorGroupKeysChanged,
} from './purchaseLineVendors';
import { parsePurchaseLinks, serializePurchaseLinks } from './purchasingLinks';
import { parseInternalHold, parsePublicHold, publicHoldLabel, requesterHoldMessage } from './purchasingHolds';
import { canHoldPurchaseStatus, canMarkPurchasedStatus } from './purchasingStatus';
import { describePurchase, notifyPurchaseRequisition } from './purchasingNotifications';
import {
  purchaseFollowsSupplierBill,
  shouldAwardLinkedOtherQuote,
  supplierBillPaymentClosesPurchase,
} from './purchaseOrderIssue';
import { supplierBillsPurchasedAmount } from './supplierBills';
import {
  awardLinkedOtherQuote,
  recordAchPayableForIssuedPurchase,
  syncIssuedPurchasePayableAmount,
} from './purchaseOrderIssueActions';
import { resolvePaymentMethodId } from './purchasingPolicy';
import { purchaseActualWithinTolerance, roundPurchaseUsd } from './purchaseActualAmount';
import { chicagoCalendarYear, formatPurchasingDocNumber, nextPurchasingCounters } from './purchasingNumbers';
import { sanitizePurchaseWrite } from './purchaseWrite';
import {
  hasPurchaseReceiptProof,
  withoutPurchaseAttachment,
} from './purchaseAttachments';
import {
  isPurchaseJobPlBucket,
  isPurchaseOverheadGroup,
  isPurchaseSpendClass,
  resolveSpendClass,
  validatePurchaseCostObject,
} from './purchasingSpendClass';

export {
  hasPurchaseReceipt,
  hasPurchaseReceiptProof,
  purchaseReceipts,
  purchaseSupportingFiles,
  withoutPurchaseAttachment,
} from './purchaseAttachments';

export const PURCHASE_REQUISITIONS_COLLECTION = 'purchaseRequisitions';
export const PURCHASE_INTERNAL_COLLECTION = 'internal';
export const PURCHASE_INTERNAL_REVIEW_DOC = 'review';
export const PURCHASING_NUMBERING_DOC = 'purchasingNumbering';

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

function asDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function asDateRequired(value: unknown): Date {
  return asDate(value) || new Date();
}

function parseSteps(raw: unknown): PurchaseApprovalStep[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): PurchaseApprovalStep | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      if (typeof row.userId !== 'string' || typeof row.gate !== 'string') return null;
      return {
        gate: row.gate as PurchaseApprovalStep['gate'],
        roleLabel: typeof row.roleLabel === 'string' ? row.roleLabel : String(row.gate),
        userId: row.userId,
        userName: typeof row.userName === 'string' ? row.userName : '',
        status: (typeof row.status === 'string' ? row.status : 'pending') as PurchaseApprovalStep['status'],
        decidedAt: asDate(row.decidedAt),
        note: typeof row.note === 'string' ? row.note : undefined,
      };
    })
    .filter((s): s is PurchaseApprovalStep => !!s);
}

function parsePurchaseVariance(raw: unknown): PurchaseVarianceRequest | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  if (typeof row.proposedAmount !== 'number' || !Number.isFinite(row.proposedAmount)) return undefined;
  if (typeof row.reviewerId !== 'string' || !row.reviewerId) return undefined;
  return {
    proposedAmount: roundPurchaseUsd(row.proposedAmount),
    requestedById: typeof row.requestedById === 'string' ? row.requestedById : '',
    requestedByName: typeof row.requestedByName === 'string' ? row.requestedByName : '',
    requestedAt: asDate(row.requestedAt) || new Date(),
    reviewerId: row.reviewerId,
    reviewerName: typeof row.reviewerName === 'string' ? row.reviewerName : '',
  };
}

function parseAmountEditHistory(raw: unknown): PurchaseAmountEditAuditEntry[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  const out: PurchaseAmountEditAuditEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const previousAmount = Number(row.previousAmount);
    const nextAmount = Number(row.nextAmount);
    if (!Number.isFinite(previousAmount) || !Number.isFinite(nextAmount)) continue;
    const note = typeof row.note === 'string' ? row.note.trim() : '';
    if (!note) continue;
    const kind =
      row.kind === 'authority_confirm' || row.kind === 'unchanged_fields' || row.kind === 'within_tolerance'
        ? row.kind
        : 'within_tolerance';
    const previousPurchasedAmount =
      row.previousPurchasedAmount == null || row.previousPurchasedAmount === ''
        ? null
        : Number(row.previousPurchasedAmount);
    const nextPurchasedAmount =
      row.nextPurchasedAmount == null || row.nextPurchasedAmount === ''
        ? null
        : Number(row.nextPurchasedAmount);
    out.push({
      at: asDate(row.at) || new Date(),
      byId: typeof row.byId === 'string' ? row.byId : '',
      byName: typeof row.byName === 'string' ? row.byName : '',
      note,
      previousAmount: roundPurchaseUsd(previousAmount),
      nextAmount: roundPurchaseUsd(nextAmount),
      previousPurchasedAmount:
        previousPurchasedAmount != null && Number.isFinite(previousPurchasedAmount)
          ? roundPurchaseUsd(previousPurchasedAmount)
          : null,
      nextPurchasedAmount:
        nextPurchasedAmount != null && Number.isFinite(nextPurchasedAmount)
          ? roundPurchaseUsd(nextPurchasedAmount)
          : null,
      kind,
    });
  }
  return out.length ? out : undefined;
}

function parseCostObject(raw: unknown): PurchaseCostObject {
  if (!raw || typeof raw !== 'object') return { type: 'overhead' };
  const row = raw as Record<string, unknown>;
  const spendClass = isPurchaseSpendClass(row.spendClass) ? row.spendClass : undefined;
  const type =
    row.type === 'project' || spendClass === 'job'
      ? 'project'
      : 'overhead';
  return {
    type,
    ...(spendClass ? { spendClass } : {}),
    ...(isPurchaseOverheadGroup(row.overheadGroup) ? { overheadGroup: row.overheadGroup } : {}),
    ...(isPurchaseJobPlBucket(row.plBucket) ? { plBucket: row.plBucket } : {}),
    ...(typeof row.dealId === 'string' && row.dealId ? { dealId: row.dealId } : {}),
    ...(typeof row.projectId === 'string' && row.projectId ? { projectId: row.projectId } : {}),
    ...(typeof row.label === 'string' && row.label ? { label: row.label } : {}),
    ...(parseForecastLineId(row.overheadExpenseId)
      ? { overheadExpenseId: parseForecastLineId(row.overheadExpenseId) }
      : {}),
  };
}

function parseForecastLineId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const id = value.trim();
  return id || undefined;
}

function denormalizedCostFields(costObject: PurchaseCostObject) {
  const spendClass = resolveSpendClass(costObject);
  return {
    spendClass,
    dealId: costObject.dealId || null,
    projectId: costObject.projectId || null,
    overheadGroup: costObject.overheadGroup || null,
    plBucket: costObject.plBucket || null,
    overheadExpenseId: costObject.overheadExpenseId?.trim() || null,
  };
}

function assertValidCostObject(costObject: PurchaseCostObject): void {
  const error = validatePurchaseCostObject(costObject);
  if (error) throw new Error(error);
}

function parseIdList(raw: unknown, fallback?: unknown): string[] | undefined {
  const ids: string[] = [];
  const seen = new Set<string>();
  const push = (value: unknown) => {
    if (typeof value !== 'string') return;
    const id = value.trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  if (Array.isArray(raw)) raw.forEach(push);
  push(fallback);
  return ids.length ? ids : undefined;
}

function parseAttachments(raw: unknown): PurchaseAttachment[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): PurchaseAttachment | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      if (typeof row.name !== 'string' || typeof row.storagePath !== 'string') return null;
      return {
        name: row.name,
        storagePath: row.storagePath,
        contentType: typeof row.contentType === 'string' ? row.contentType : undefined,
        size: typeof row.size === 'number' ? row.size : undefined,
        url: typeof row.url === 'string' ? row.url : undefined,
        kind: row.kind === 'receipt' || row.kind === 'quote' ? row.kind : undefined,
        contentHash: typeof row.contentHash === 'string' ? row.contentHash : undefined,
        attachedById: typeof row.attachedById === 'string' ? row.attachedById : undefined,
        attachedByName: typeof row.attachedByName === 'string' ? row.attachedByName : undefined,
        attachedViaAgentId: typeof row.attachedViaAgentId === 'string' ? row.attachedViaAgentId : undefined,
        attachedAt: typeof row.attachedAt === 'string' ? row.attachedAt : undefined,
      };
    })
    .filter((a): a is PurchaseAttachment => !!a);
}

export function parsePurchaseRequisition(id: string, data: Record<string, unknown>): PurchaseRequisition {
  const steps = parseSteps(data.approvalSteps);
  const title = typeof data.title === 'string' ? data.title : '';
  const lineItems = parsePurchaseLineItems(data.lineItems, {
    title,
    amount: Number(data.amount) || 0,
  });
  const salesTaxPct = typeof data.salesTaxPct === 'number' && Number.isFinite(data.salesTaxPct)
    ? data.salesTaxPct
    : 0;
  const amount = lineItems.length ? requisitionTotal(lineItems, salesTaxPct) : Number(data.amount) || 0;
  const costObject = parseCostObject(data.costObject);
  return {
    id,
    prNumber: typeof data.prNumber === 'string' ? data.prNumber : id,
    poNumber: typeof data.poNumber === 'string' ? data.poNumber : undefined,
    requesterId: typeof data.requesterId === 'string' ? data.requesterId : '',
    requesterName: typeof data.requesterName === 'string' ? data.requesterName : '',
    title: title || summarizeRequisitionTitle(lineItems),
    justification: typeof data.justification === 'string' ? data.justification : '',
    vendorName: typeof data.vendorName === 'string' ? data.vendorName : undefined,
    amount,
    approvedAmount:
      typeof data.approvedAmount === 'number' && Number.isFinite(data.approvedAmount)
        ? roundPurchaseUsd(data.approvedAmount)
        : undefined,
    salesTaxPct: typeof data.salesTaxPct === 'number' ? data.salesTaxPct : undefined,
    lineItems,
    links: parsePurchaseLinks(data.links),
    costObject,
    spendClass: isPurchaseSpendClass(data.spendClass) ? data.spendClass : resolveSpendClass(costObject),
    dealId: typeof data.dealId === 'string' ? data.dealId : costObject.dealId,
    plBucket: isPurchaseJobPlBucket(data.plBucket) ? data.plBucket : costObject.plBucket,
    overheadGroup: isPurchaseOverheadGroup(data.overheadGroup) ? data.overheadGroup : costObject.overheadGroup,
    overheadExpenseId: parseForecastLineId(data.overheadExpenseId) || costObject.overheadExpenseId,
    attachments: parseAttachments(data.attachments),
    status: (typeof data.status === 'string' ? data.status : 'pending_approval') as PurchaseRequisitionStatus,
    approvalSteps: steps,
    currentStepIndex: typeof data.currentStepIndex === 'number' ? data.currentStepIndex : currentPendingStepIndex(steps),
    approvalStepUserIds: Array.isArray(data.approvalStepUserIds)
      ? data.approvalStepUserIds.filter((x): x is string => typeof x === 'string')
      : steps.map(s => s.userId),
    currentApproverId: typeof data.currentApproverId === 'string' ? data.currentApproverId : currentPendingStep(steps)?.userId,
    bandLabel: typeof data.bandLabel === 'string' ? data.bandLabel : undefined,
    paymentMethod: typeof data.paymentMethod === 'string' ? data.paymentMethod : undefined,
    paymentMethodId: typeof data.paymentMethodId === 'string' ? data.paymentMethodId : undefined,
    paymentMethodNote: typeof data.paymentMethodNote === 'string' ? data.paymentMethodNote : undefined,
    rfqSubmissionId: typeof data.rfqSubmissionId === 'string' ? data.rfqSubmissionId : undefined,
    rfqProjectId: typeof data.rfqProjectId === 'string' ? data.rfqProjectId : undefined,
    designerInvoiceId: typeof data.designerInvoiceId === 'string' ? data.designerInvoiceId : undefined,
    designerInvoiceNumber:
      typeof data.designerInvoiceNumber === 'string' ? data.designerInvoiceNumber : undefined,
    createSupplierBill: data.createSupplierBill === false ? false : data.createSupplierBill === true ? true : undefined,
    origin:
      data.origin === 'employee_recommendation' ||
      data.origin === 'other_quotes' ||
      data.origin === 'designer_invoice' ||
      data.origin === 'manual'
        ? data.origin
        : undefined,
    crewReportKind:
      data.crewReportKind === 'damaged_tool' ||
      data.crewReportKind === 'wish_list' ||
      data.crewReportKind === 'purchasable'
        ? data.crewReportKind
        : undefined,
    source: typeof data.source === 'string' ? data.source : undefined,
    createdViaAgent: data.createdViaAgent === true,
    createdByAgentId: typeof data.createdByAgentId === 'string' ? data.createdByAgentId : undefined,
    alreadyPurchased: data.alreadyPurchased === true,
    badge: typeof data.badge === 'string' && data.badge.trim() ? data.badge.trim() : undefined,
    qbBillStatus: isPurchaseQbBillStatus(data.qbBillStatus) ? data.qbBillStatus : undefined,
    qbBillId: typeof data.qbBillId === 'string' ? data.qbBillId : undefined,
    qbBillUrl: typeof data.qbBillUrl === 'string' ? data.qbBillUrl : undefined,
    qbBillMessage: typeof data.qbBillMessage === 'string' ? data.qbBillMessage : undefined,
    hold: parsePublicHold(data.hold),
    purchasedAt: asDate(data.purchasedAt),
    purchasedById: typeof data.purchasedById === 'string' ? data.purchasedById : undefined,
    purchasedByName: typeof data.purchasedByName === 'string' ? data.purchasedByName : undefined,
    purchasedAmount:
      typeof data.purchasedAmount === 'number' && Number.isFinite(data.purchasedAmount)
        ? roundPurchaseUsd(data.purchasedAmount)
        : undefined,
    purchasedVia: data.purchasedVia === 'supplier_bill' ? 'supplier_bill' : undefined,
    amountEditHistory: parseAmountEditHistory(data.amountEditHistory),
    qbExpenseId: typeof data.qbExpenseId === 'string' ? data.qbExpenseId : undefined,
    qbExpenseIds: parseIdList(data.qbExpenseIds, data.qbExpenseId),
    sequenceTransactionId: typeof data.sequenceTransactionId === 'string' ? data.sequenceTransactionId : undefined,
    sequenceTransactionIds: parseIdList(data.sequenceTransactionIds, data.sequenceTransactionId),
    purchaseVariance: parsePurchaseVariance(data.purchaseVariance),
    createdAt: asDateRequired(data.createdAt),
    updatedAt: asDateRequired(data.updatedAt),
    approvedAt: asDate(data.approvedAt),
    cancelledAt: asDate(data.cancelledAt),
    cancelledById: typeof data.cancelledById === 'string' ? data.cancelledById : undefined,
    cancelledByName: typeof data.cancelledByName === 'string' ? data.cancelledByName : undefined,
  };
}

function serializeSteps(steps: PurchaseApprovalStep[]) {
  return steps.map(s => ({
    gate: s.gate,
    roleLabel: s.roleLabel,
    userId: s.userId,
    userName: s.userName,
    status: s.status,
    ...(s.decidedAt ? { decidedAt: Timestamp.fromDate(s.decidedAt) } : {}),
    ...(s.note ? { note: s.note } : {}),
  }));
}

async function allocatePurchasingNumber(kind: 'pr' | 'po'): Promise<{ year: number; n: number; formatted: string }> {
  const batch = await allocatePurchasingNumbers({
    prCount: kind === 'pr' ? 1 : 0,
    poCount: kind === 'po' ? 1 : 0,
  });
  if (kind === 'pr') {
    return { year: batch.year, n: batch.prStart, formatted: batch.prNumber! };
  }
  return { year: batch.year, n: batch.poStart, formatted: batch.poNumbers[0]! };
}

/**
 * One counter transaction for N PO numbers (and optional PR).
 * CRM #73: multi-store approve must not interleave with other allocs mid-loop,
 * and year rollover must not rewind the other series via a partial write.
 */
async function allocatePurchasingNumbers(args: {
  prCount?: number;
  poCount?: number;
}): Promise<{
  year: number;
  prStart: number;
  poStart: number;
  prNumber?: string;
  poNumbers: string[];
}> {
  const prCount = Math.max(0, Math.round(args.prCount || 0));
  const poCount = Math.max(0, Math.round(args.poCount || 0));
  const counterRef = doc(db, 'settings', PURCHASING_NUMBERING_DOC);
  return runTransaction(db, async tx => {
    const snap = await tx.get(counterRef);
    const year = chicagoCalendarYear();
    const data = snap.exists() ? snap.data() : {};
    const counters = nextPurchasingCounters({
      storedYear: Number(data?.year),
      year,
      nextPr: data?.nextPr,
      nextPo: data?.nextPo,
      prCount,
      poCount,
    });
    tx.set(
      counterRef,
      {
        year: counters.year,
        nextPr: counters.nextPr,
        nextPo: counters.nextPo,
        updatedAt: Timestamp.now(),
      },
      { merge: true },
    );
    const poNumbers = poCount
      ? Array.from({ length: poCount }, (_, i) =>
          formatPurchasingDocNumber('PO', counters.year, counters.poStart + i),
        )
      : [];
    return {
      year: counters.year,
      prStart: counters.prStart,
      poStart: counters.poStart,
      ...(prCount
        ? { prNumber: formatPurchasingDocNumber('PR', counters.year, counters.prStart) }
        : {}),
      poNumbers,
    };
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/**
 * Admin-SDK upload via Cloud Function (CRM #72 / #74). Used when client Storage
 * denies staff uploads so Mark purchased is not blocked by storage/unauthorized.
 */
async function uploadPurchaseAttachmentViaCallable(
  prId: string,
  file: File,
  kind: PurchaseAttachmentKind,
): Promise<PurchaseAttachment> {
  const functions = getFunctions(undefined, 'us-central1');
  const upload = httpsCallable<
    {
      purchaseRequisitionId: string;
      fileName: string;
      fileBase64: string;
      contentType?: string;
      kind: PurchaseAttachmentKind;
    },
    { success?: boolean; attachment?: PurchaseAttachment }
  >(functions, 'uploadPurchaseRequisitionAttachment');
  const result = await upload({
    purchaseRequisitionId: prId,
    fileName: file.name,
    fileBase64: await fileToBase64(file),
    contentType: file.type || undefined,
    kind,
  });
  const attachment = result.data?.attachment;
  if (!attachment?.storagePath) {
    throw new Error('Receipt upload failed. Try again, or ask an admin if it keeps failing.');
  }
  const contentType = attachment.contentType || file.type || undefined;
  return {
    name: attachment.name || file.name,
    storagePath: attachment.storagePath,
    ...(contentType ? { contentType } : {}),
    size: attachment.size ?? file.size,
    ...(attachment.url ? { url: attachment.url } : {}),
    kind: attachment.kind === 'receipt' || attachment.kind === 'quote' ? attachment.kind : kind,
  };
}

export async function uploadPurchaseAttachment(
  prId: string,
  file: File,
  kind: PurchaseAttachmentKind = 'quote',
): Promise<PurchaseAttachment> {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new Error(`${file.name} is larger than 15 MB.`);
  }
  await ensureCrmStorageAccessBeforeUpload();
  const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(0, 120) || kind;
  const folder = kind === 'receipt' ? 'receipts' : 'quotes';
  const storagePath = `purchase-requisitions/${prId}/${folder}/${Date.now()}_${safeName}`;
  const ref = storageRef(storage, storagePath);
  try {
    await uploadBytes(ref, file, { contentType: file.type || 'application/octet-stream' });
  } catch (err) {
    // CRM #72 / #74 / #80: staff often still hit storage/unauthorized when the JWT claim
    // CF was never deployed — fall back to Admin SDK callable instead of failing silently.
    if (isStorageUnauthorizedError(err)) {
      try {
        return await uploadPurchaseAttachmentViaCallable(prId, file, kind);
      } catch (callableErr) {
        const code =
          callableErr && typeof callableErr === 'object' && 'code' in callableErr
            ? String((callableErr as { code?: unknown }).code || '')
            : '';
        if (code === 'functions/not-found' || code === 'functions/unavailable') {
          throw new Error(
            `${purchaseAttachmentUploadErrorMessage(err, file.name)} ` +
              `(server helper ${code || 'unavailable'} — deploy uploadPurchaseRequisitionAttachment.)`,
          );
        }
        throw new Error(purchaseCallableErrorMessage(callableErr, file.name));
      }
    }
    throw new Error(purchaseAttachmentUploadErrorMessage(err, file.name));
  }
  const url = await getDownloadURL(ref);
  return {
    name: file.name,
    storagePath,
    ...(file.type ? { contentType: file.type } : {}),
    size: file.size,
    url,
    kind,
  };
}

async function uploadPurchaseReceipts(prId: string, files: File[]): Promise<PurchaseAttachment[]> {
  const receipts: PurchaseAttachment[] = [];
  for (const file of files) {
    receipts.push(await uploadPurchaseAttachment(prId, file, 'receipt'));
  }
  return receipts;
}

function preparedLines(input: {
  lineItems?: PurchaseLineItem[];
  title?: string;
  amount?: number;
  salesTaxPct?: number;
}): {
  lineItems: PurchaseLineItem[];
  title: string;
  amount: number;
  salesTaxPct: number;
} {
  const salesTaxPct = Number.isFinite(input.salesTaxPct) ? Number(input.salesTaxPct) : 0;
  const lineItems = input.lineItems?.length
    ? input.lineItems
    : parsePurchaseLineItems(undefined, { title: input.title, amount: input.amount });
  const amount = requisitionTotal(lineItems, salesTaxPct);
  const title = summarizeRequisitionTitle(lineItems) || input.title?.trim() || '';
  return { lineItems, title, amount, salesTaxPct };
}

export interface SubmitPurchaseRequisitionInput {
  requesterId: string;
  requesterName: string;
  title?: string;
  justification: string;
  vendorName?: string;
  amount?: number;
  lineItems: PurchaseLineItem[];
  links?: PurchaseLink[];
  salesTaxPct?: number;
  costObject: PurchaseCostObject;
  steps: PurchaseApprovalStep[];
  bandLabel?: string;
  files?: File[];
  existingAttachments?: PurchaseAttachment[];
  issueNow?: boolean;
  paymentMethod?: string;
  paymentMethodId?: string;
  paymentMethodNote?: string;
  rfqSubmissionId?: string;
  rfqProjectId?: string;
  designerInvoiceId?: string;
  designerInvoiceNumber?: string;
  /** Explicit false skips ACH payable / supplierBills on issueNow. */
  createSupplierBill?: boolean;
}

export async function submitPurchaseRequisition(input: SubmitPurchaseRequisitionInput): Promise<string> {
  if (!input.requesterId) throw new Error('Sign in to submit a requisition.');
  if (!input.justification.trim()) throw new Error('Add a justification.');
  assertValidCostObject(input.costObject);
  const prepared = preparedLines(input);
  if (!prepared.lineItems.length) throw new Error('Add at least one line item.');
  if (!prepared.title) throw new Error('Describe what you are buying.');
  if (!Number.isFinite(prepared.amount) || prepared.amount <= 0) throw new Error('Enter an amount greater than zero.');
  if (!input.steps.length) throw new Error('No approver could be resolved for this request.');
  if (input.issueNow && !input.paymentMethod?.trim()) throw new Error('Choose an approved payment method.');

  const ref = doc(collection(db, PURCHASE_REQUISITIONS_COLLECTION));
  const attachments: PurchaseAttachment[] = [...(input.existingAttachments || [])];
  for (const file of input.files || []) {
    attachments.push(await uploadPurchaseAttachment(ref.id, file));
  }

  const allocated = await allocatePurchasingNumber('pr');
  const pending = currentPendingStep(input.steps);
  const now = Timestamp.now();

  await setDoc(ref, sanitizePurchaseWrite({
    prNumber: allocated.formatted,
    requesterId: input.requesterId,
    requesterName: input.requesterName,
    title: prepared.title,
    justification: input.justification.trim(),
    vendorName: input.vendorName?.trim() || null,
    amount: prepared.amount,
    salesTaxPct: prepared.salesTaxPct,
    lineItems: serializePurchaseLineItems(prepared.lineItems, prepared.salesTaxPct),
    links: serializePurchaseLinks(input.links || []),
    costObject: input.costObject,
    ...denormalizedCostFields(input.costObject),
    attachments,
    status: 'pending_approval',
    approvalSteps: serializeSteps(input.steps),
    currentStepIndex: currentPendingStepIndex(input.steps),
    approvalStepUserIds: input.steps.map(s => s.userId),
    currentApproverId: pending?.userId || null,
    bandLabel: input.bandLabel || null,
    paymentMethod: input.paymentMethod?.trim() || null,
    paymentMethodId: input.paymentMethodId?.trim() || null,
    paymentMethodNote: input.paymentMethodNote?.trim() || null,
    rfqSubmissionId: input.rfqSubmissionId?.trim() || null,
    rfqProjectId: input.rfqProjectId?.trim() || null,
    designerInvoiceId: input.designerInvoiceId?.trim() || null,
    designerInvoiceNumber: input.designerInvoiceNumber?.trim() || null,
    createSupplierBill: input.createSupplierBill === false ? false : input.createSupplierBill === true ? true : null,
    qbBillStatus: input.designerInvoiceId?.trim() ? 'pending' : null,
    createdAt: now,
    updatedAt: now,
  }));

  if (input.issueNow) {
    await approvePurchaseRequisition({
      requisition: parsePurchaseRequisition(ref.id, {
        prNumber: allocated.formatted,
        requesterId: input.requesterId,
        requesterName: input.requesterName,
        title: prepared.title,
        justification: input.justification.trim(),
        vendorName: input.vendorName?.trim() || null,
        amount: prepared.amount,
        salesTaxPct: prepared.salesTaxPct,
        lineItems: serializePurchaseLineItems(prepared.lineItems, prepared.salesTaxPct),
        links: serializePurchaseLinks(input.links || []),
        costObject: input.costObject,
        attachments,
        status: 'pending_approval',
        approvalSteps: serializeSteps(input.steps),
        currentStepIndex: currentPendingStepIndex(input.steps),
        approvalStepUserIds: input.steps.map(s => s.userId),
        currentApproverId: pending?.userId || null,
        bandLabel: input.bandLabel || null,
        paymentMethod: input.paymentMethod?.trim() || null,
        paymentMethodId: input.paymentMethodId?.trim() || null,
        paymentMethodNote: input.paymentMethodNote?.trim() || null,
        rfqSubmissionId: input.rfqSubmissionId?.trim() || null,
        rfqProjectId: input.rfqProjectId?.trim() || null,
        designerInvoiceId: input.designerInvoiceId?.trim() || null,
        designerInvoiceNumber: input.designerInvoiceNumber?.trim() || null,
        createSupplierBill: input.createSupplierBill === false ? false : input.createSupplierBill === true ? true : null,
        qbBillStatus: input.designerInvoiceId?.trim() ? 'pending' : null,
        createdAt: now,
        updatedAt: now,
      }),
      actorId: input.requesterId,
      actorName: input.requesterName,
      paymentMethod: input.paymentMethod,
      paymentMethodId: input.paymentMethodId,
      paymentMethodNote: input.paymentMethodNote,
    });
    return ref.id;
  }

  if (pending) {
    await notifyPurchaseRequisition({
      userId: pending.userId,
      title: 'Purchase requisition needs your approval',
      message: `${input.requesterName} submitted ${describePurchase({
        amount: prepared.amount,
        title: prepared.title,
        vendorName: input.vendorName,
        costLabel: input.costObject.label,
      })}`,
      prId: ref.id,
      skipUserId: input.requesterId,
    });
  }

  return ref.id;
}

export interface ResubmitPurchaseRequisitionInput {
  id: string;
  actorId: string;
  title?: string;
  justification: string;
  vendorName?: string;
  amount?: number;
  lineItems: PurchaseLineItem[];
  links?: PurchaseLink[];
  salesTaxPct?: number;
  costObject: PurchaseCostObject;
  steps: PurchaseApprovalStep[];
  bandLabel?: string;
  files?: File[];
  existingAttachments?: PurchaseAttachment[];
  /** Defaults to the resubmit wording. Drafts use the first-submit title. */
  notifyTitle?: string;
  issueNow?: boolean;
  paymentMethod?: string;
  paymentMethodId?: string;
  paymentMethodNote?: string;
}

/**
 * Point an overhead PO at a known forecast line, or clear that link so the PO
 * draws the OpEx group budget. Does not change the overhead group.
 */
export async function assignPurchaseForecastLine(input: {
  requisition: PurchaseRequisition;
  /** overheadExpenses id. Null or blank returns the PO to the group budget. */
  overheadExpenseId: string | null;
}): Promise<void> {
  if (resolveSpendClass(input.requisition.costObject) !== 'overhead') {
    throw new Error('Only overhead purchases can pay a forecast line.');
  }
  const id = input.overheadExpenseId?.trim() || '';
  const costObject: PurchaseCostObject = { ...input.requisition.costObject };
  delete costObject.overheadExpenseId;
  if (id) costObject.overheadExpenseId = id;
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, input.requisition.id), sanitizePurchaseWrite({
    costObject,
    overheadExpenseId: id ? id : deleteField(),
    updatedAt: serverTimestamp(),
  }));
}

export function subscribeActiveOverheadForecastLines(
  onData: (rows: Array<{ id: string; name: string }>) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, 'overheadExpenses'),
    snap => {
      const rows = snap.docs
        .map(item => {
          const data = item.data() as { name?: unknown; isActive?: unknown };
          return {
            id: item.id,
            name: typeof data.name === 'string' ? data.name.trim() : '',
            active: data.isActive !== false,
          };
        })
        .filter(row => row.active && row.name)
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      onData(rows);
    },
    err => onError?.(err),
  );
}

export async function resubmitPurchaseRequisition(input: ResubmitPurchaseRequisitionInput): Promise<void> {
  if (!input.steps.length) throw new Error('No approver could be resolved for this request.');
  assertValidCostObject(input.costObject);
  const prepared = preparedLines(input);
  if (!prepared.lineItems.length) throw new Error('Add at least one line item.');
  if (!Number.isFinite(prepared.amount) || prepared.amount <= 0) throw new Error('Enter an amount greater than zero.');
  if (input.issueNow && !input.paymentMethod?.trim()) throw new Error('Choose an approved payment method.');
  const extra: PurchaseAttachment[] = [];
  for (const file of input.files || []) {
    extra.push(await uploadPurchaseAttachment(input.id, file));
  }
  const pending = currentPendingStep(input.steps);
  // CRM #73: never resubmit with leftover per-store PO stamps from a prior issue.
  const resubmitLines = clearLinePoNumbers(prepared.lineItems);
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, input.id), sanitizePurchaseWrite({
    title: prepared.title,
    justification: input.justification.trim(),
    vendorName: input.vendorName?.trim() || null,
    amount: prepared.amount,
    salesTaxPct: prepared.salesTaxPct,
    lineItems: serializePurchaseLineItems(resubmitLines, prepared.salesTaxPct),
    links: serializePurchaseLinks(input.links || []),
    costObject: input.costObject,
    ...denormalizedCostFields(input.costObject),
    attachments: [...(input.existingAttachments || []), ...extra],
    status: 'pending_approval',
    approvalSteps: serializeSteps(input.steps),
    currentStepIndex: currentPendingStepIndex(input.steps),
    approvalStepUserIds: input.steps.map(s => s.userId),
    currentApproverId: pending?.userId || null,
    bandLabel: input.bandLabel || null,
    paymentMethod: deleteField(),
    paymentMethodId: deleteField(),
    paymentMethodNote: deleteField(),
    poNumber: deleteField(),
    approvedAt: deleteField(),
    approvedAmount: deleteField(),
    hold: deleteField(),
    updatedAt: serverTimestamp(),
  }));
  if (input.issueNow) {
    const attachments = [...(input.existingAttachments || []), ...extra];
    await approvePurchaseRequisition({
      requisition: parsePurchaseRequisition(input.id, {
        requesterId: input.actorId,
        title: prepared.title,
        justification: input.justification.trim(),
        vendorName: input.vendorName?.trim() || null,
        amount: prepared.amount,
        salesTaxPct: prepared.salesTaxPct,
        lineItems: serializePurchaseLineItems(resubmitLines, prepared.salesTaxPct),
        links: serializePurchaseLinks(input.links || []),
        costObject: input.costObject,
        attachments,
        status: 'pending_approval',
        approvalSteps: serializeSteps(input.steps),
        currentStepIndex: currentPendingStepIndex(input.steps),
        approvalStepUserIds: input.steps.map(s => s.userId),
        currentApproverId: pending?.userId || null,
        bandLabel: input.bandLabel || null,
        createdAt: new Date(),
        updatedAt: new Date(),
      }),
      actorId: input.actorId,
      actorName: input.actorId,
      paymentMethod: input.paymentMethod,
      paymentMethodId: input.paymentMethodId,
      paymentMethodNote: input.paymentMethodNote,
    });
    return;
  }
  if (pending) {
    await notifyPurchaseRequisition({
      userId: pending.userId,
      title: input.notifyTitle || 'Purchase requisition resubmitted',
      message: describePurchase({
        amount: prepared.amount,
        title: prepared.title,
        vendorName: input.vendorName,
        costLabel: input.costObject.label,
      }),
      prId: input.id,
      skipUserId: input.actorId,
    });
  }
}

export interface ReviseIssuedPurchaseAmountInput {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  access: PurchasingActorAccess;
  justification: string;
  vendorName?: string;
  lineItems: PurchaseLineItem[];
  salesTaxPct?: number;
  steps: PurchaseApprovalStep[];
  bandLabel?: string;
  issueNow?: boolean;
  paymentMethod?: string;
  paymentMethodId?: string;
  paymentMethodNote?: string;
}

export async function reviseIssuedPurchaseAmount(
  input: ReviseIssuedPurchaseAmountInput,
): Promise<'updated' | 'resubmitted'> {
  const { requisition } = input;
  if (!canReviseIssuedPurchaseAmount(input.access, requisition, input.actorId)) {
    throw new Error('You cannot change the amount on this purchase order.');
  }
  if (!input.justification.trim()) throw new Error('Add a justification.');
  const prepared = preparedLines({
    lineItems: input.lineItems,
    title: requisition.title,
    amount: requisition.amount,
    salesTaxPct: input.salesTaxPct,
  });
  if (!prepared.lineItems.length) throw new Error('Add at least one line item.');
  if (!Number.isFinite(prepared.amount) || prepared.amount <= 0) throw new Error('Enter an amount greater than zero.');

  const baseline = approvedPurchaseAmountBaseline(requisition);
  const kind = issuedPurchaseAmountEditKind(baseline, prepared.amount);
  const headerVendor = input.vendorName?.trim() || null;
  const storesChanged = vendorGroupKeysChanged(
    requisition.lineItems,
    requisition.vendorName,
    prepared.lineItems,
    headerVendor,
  );
  if (kind === 'unchanged' && !storesChanged) {
    throw new Error('The amount is unchanged.');
  }
  if (kind === 'needs_approval') {
    await resubmitPurchaseRequisition({
      id: requisition.id,
      actorId: input.actorId,
      title: prepared.title,
      justification: input.justification,
      vendorName: input.vendorName,
      lineItems: prepared.lineItems,
      links: requisition.links,
      salesTaxPct: prepared.salesTaxPct,
      costObject: requisition.costObject,
      steps: input.steps,
      bandLabel: input.bandLabel,
      existingAttachments: requisition.attachments,
      issueNow: input.issueNow,
      paymentMethod: input.paymentMethod,
      paymentMethodId: input.paymentMethodId,
      paymentMethodNote: input.paymentMethodNote,
    });
    return 'resubmitted';
  }

  // CRM #73: store/vendor fork change on an issued PO needs fresh unique numbers.
  let nextLines: PurchaseLineItem[] = prepared.lineItems;
  let nextHeaderVendor = headerVendor || undefined;
  let headerPoPatch: Record<string, unknown> = {};
  if (storesChanged) {
    const cleanLines = clearLinePoNumbers(prepared.lineItems);
    const groups = groupLinesByVendor(cleanLines, headerVendor);
    const count = Math.max(1, groups.length);
    const issuedPoNumbers = (await allocatePurchasingNumbers({ poCount: count })).poNumbers;
    nextLines = assignPoNumbersToLines(cleanLines, headerVendor, issuedPoNumbers) as PurchaseLineItem[];
    const singleVendor = groups.length <= 1 ? groups[0]?.vendorName || headerVendor || '' : '';
    nextHeaderVendor = singleVendor || undefined;
    headerPoPatch =
      issuedPoNumbers.length === 1
        ? { poNumber: issuedPoNumbers[0] }
        : { poNumber: deleteField() };
  }

  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id), {
    title: prepared.title,
    justification: input.justification.trim(),
    vendorName: nextHeaderVendor?.trim() || null,
    amount: prepared.amount,
    salesTaxPct: prepared.salesTaxPct,
    lineItems: serializePurchaseLineItems(nextLines, prepared.salesTaxPct),
    ...headerPoPatch,
    approvedAmount: baseline,
    updatedAt: serverTimestamp(),
  });
  await syncIssuedPurchasePayableAmount({
    requisitionId: requisition.id,
    amount: prepared.amount,
    description: prepared.title,
  }).catch(err => {
    console.warn('[purchaseRequisitions] failed to sync payable amount', err);
  });
  return 'updated';
}

export interface EditPurchasedPurchaseAmountsInput {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  access: PurchasingActorAccess;
  note: string;
  /** When omitted, keeps the current PO amount. */
  amount?: number;
  /** When omitted, keeps the current purchasedAmount (or leaves unset). */
  purchasedAmount?: number | null;
  /**
   * Required when the probe amount is more than 10% off the approved baseline
   * (same rule as Adjust amount on an issued PO). Override roles may set this.
   */
  authorityConfirm?: boolean;
}

/**
 * Edit amount and/or purchasedAmount on a purchased PO.
 * Does not overwrite purchasedAt / purchasedBy*. Requires a note; writes amountEditHistory.
 */
export async function editPurchasedPurchaseAmounts(
  input: EditPurchasedPurchaseAmountsInput,
): Promise<{ kind: 'within_tolerance' | 'unchanged_fields' | 'authority_confirm' }> {
  const { requisition } = input;
  if (!canEditPurchasedPurchaseAmounts(input.access, requisition, input.actorId)) {
    throw new Error('You cannot edit amounts on this purchased order.');
  }
  const note = input.note.trim();
  if (!note) throw new Error('Add a note explaining the change.');

  const nextAmount =
    input.amount != null && Number.isFinite(Number(input.amount))
      ? roundPurchaseUsd(Number(input.amount))
      : roundPurchaseUsd(requisition.amount);
  if (!(nextAmount > 0)) throw new Error('Enter an amount greater than zero.');

  const previousPurchased =
    typeof requisition.purchasedAmount === 'number' && Number.isFinite(requisition.purchasedAmount)
      ? roundPurchaseUsd(requisition.purchasedAmount)
      : null;
  let nextPurchased: number | null = previousPurchased;
  if (input.purchasedAmount !== undefined) {
    if (input.purchasedAmount == null || input.purchasedAmount === ('' as unknown)) {
      nextPurchased = null;
    } else {
      const n = roundPurchaseUsd(Number(input.purchasedAmount));
      if (!Number.isFinite(n) || n < 0) throw new Error('Enter a valid purchased amount.');
      nextPurchased = n;
    }
  }

  const amountChanged = Math.abs(nextAmount - roundPurchaseUsd(requisition.amount)) > 0.005;
  const purchasedChanged =
    (previousPurchased == null && nextPurchased != null)
    || (previousPurchased != null && nextPurchased == null)
    || (previousPurchased != null && nextPurchased != null && Math.abs(previousPurchased - nextPurchased) > 0.005);
  if (!amountChanged && !purchasedChanged) {
    throw new Error('Nothing changed.');
  }

  const kind = purchasedAmountEditKind({
    approvedAmount: requisition.approvedAmount,
    amount: requisition.amount,
    nextAmount,
    nextPurchasedAmount: nextPurchased,
  });
  const canAuthority =
    input.authorityConfirm === true && canOverridePurchaseApproval(input.access);
  if (kind === 'needs_approval' && !canAuthority) {
    throw new Error(
      'This is more than 10% off the approved amount. An authority override is required (same rule as Adjust amount on an issued PO).',
    );
  }

  const auditKind =
    kind === 'needs_approval' ? 'authority_confirm' : kind === 'unchanged' ? 'unchanged_fields' : 'within_tolerance';
  const entry = {
    at: Timestamp.now(),
    byId: input.actorId,
    byName: input.actorName,
    note,
    previousAmount: roundPurchaseUsd(requisition.amount),
    nextAmount,
    previousPurchasedAmount: previousPurchased,
    nextPurchasedAmount: nextPurchased,
    kind: auditKind,
  };

  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id), {
    amount: nextAmount,
    purchasedAmount: nextPurchased == null ? deleteField() : nextPurchased,
    amountEditHistory: arrayUnion(entry),
    // Keep the approved baseline stable so stacked edits do not widen the 10% window.
    approvedAmount: approvedPurchaseAmountBaseline(requisition),
    updatedAt: serverTimestamp(),
  });

  if (amountChanged) {
    await syncIssuedPurchasePayableAmount({
      requisitionId: requisition.id,
      amount: nextAmount,
      description: requisition.title,
    }).catch(err => {
      console.warn('[purchaseRequisitions] failed to sync payable amount after purchased edit', err);
    });
  }

  return { kind: auditKind };
}

export async function approvePurchaseRequisition(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  note?: string;
  paymentMethod?: string;
  paymentMethodId?: string;
  paymentMethodNote?: string;
  override?: boolean;
}): Promise<void> {
  const { requisition } = params;
  const idx = currentPendingStepIndex(requisition.approvalSteps);
  if (idx < 0) throw new Error('Nothing left to approve.');
  const step = requisition.approvalSteps[idx];
  if (!params.override && step.userId !== params.actorId) {
    throw new Error('This requisition is waiting on someone else.');
  }

  const isFinal = requisition.approvalSteps.slice(idx + 1).every(s => s.status !== 'pending');
  const paymentMethod = params.paymentMethod?.trim() || requisition.paymentMethod?.trim() || '';
  const paymentMethodId =
    params.paymentMethodId?.trim()
    || requisition.paymentMethodId?.trim()
    || resolvePaymentMethodId(paymentMethod)
    || '';
  if (isFinal && !paymentMethod) {
    throw new Error('Choose an approved payment method.');
  }

  const now = new Date();
  const substituteActor = !!params.override && step.userId !== params.actorId;
  const nextSteps = requisition.approvalSteps.map((s, i) =>
    i === idx
      ? finalizePurchaseApprovalStep(
          s,
          'approved',
          { id: params.actorId, name: params.actorName },
          { note: params.note, decidedAt: now, substituteActor },
        )
      : s,
  );
  const nextPending = currentPendingStep(nextSteps);
  let poNumber: string | undefined;
  let issuedLines = requisition.lineItems;
  let issuedHeaderVendor = requisition.vendorName;
  if (isFinal) {
    // Drop any sticky line stamps before forking (CRM #73) so assign never
    // preserves numbers that already belong to other POs.
    const cleanLines = clearLinePoNumbers(requisition.lineItems);
    const groups = groupLinesByVendor(cleanLines, requisition.vendorName);
    const count = Math.max(1, groups.length);
    const issuedPoNumbers = (await allocatePurchasingNumbers({ poCount: count })).poNumbers;
    issuedLines = assignPoNumbersToLines(cleanLines, requisition.vendorName, issuedPoNumbers) as PurchaseLineItem[];
    const singleVendor = groups.length <= 1 ? groups[0]?.vendorName || requisition.vendorName || '' : '';
    issuedHeaderVendor = singleVendor || undefined;
    poNumber = issuedPoNumbers.length === 1 ? issuedPoNumbers[0] : undefined;
  }

  if (isFinal && shouldAwardLinkedOtherQuote(requisition)) {
    await awardLinkedOtherQuote({ requisition, actorName: params.actorName });
  }

  const approvalPatch: Record<string, unknown> = {
    approvalSteps: serializeSteps(nextSteps),
    currentStepIndex: nextPending ? currentPendingStepIndex(nextSteps) : idx,
    currentApproverId: nextPending?.userId || null,
    hold: deleteField(),
    status: isFinal ? 'approved' : 'in_review',
    ...(isFinal
      ? {
          ...(poNumber ? { poNumber } : { poNumber: deleteField() }),
          vendorName: issuedHeaderVendor?.trim() || null,
          lineItems: serializePurchaseLineItems(issuedLines, requisition.salesTaxPct ?? 0),
          paymentMethod,
          paymentMethodId: paymentMethodId || null,
          paymentMethodNote: params.paymentMethodNote?.trim() || requisition.paymentMethodNote || null,
          approvedAt: Timestamp.fromDate(now),
          approvedAmount: requisition.amount,
        }
      : {}),
    updatedAt: serverTimestamp(),
  };

  // POC edit log (Matthew Lynch): stamp + auditLog when the actor is allowlisted.
  try {
    const { withCrmAuditUpdate } = await import('./crmAuditLog');
    const stamped = await withCrmAuditUpdate(
      {
        uid: params.actorId,
        name: params.actorName,
        email: (params as { actorEmail?: string }).actorEmail,
      },
      {
        collection: PURCHASE_REQUISITIONS_COLLECTION,
        docId: requisition.id,
        action: 'update',
        entityType: 'purchase',
        entityId: requisition.id,
        changedFields: ['status', 'approvalSteps'],
      },
      approvalPatch,
    );
    Object.assign(approvalPatch, stamped);
  } catch (err) {
    console.warn('[purchaseRequisitions] audit stamp skipped', err);
  }

  await updateDoc(
    doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id),
    approvalPatch as Record<string, unknown> as never,
  );

  if (isFinal && poNumber) {
    await recordAchPayableForIssuedPurchase({
      requisition: {
        ...requisition,
        vendorName: issuedHeaderVendor,
        lineItems: issuedLines,
        paymentMethod,
        paymentMethodId: paymentMethodId || undefined,
      },
      poNumber,
      paymentMethod,
      paymentMethodId,
      actorId: params.actorId,
      actorName: params.actorName,
    }).catch(err => {
      console.warn('[purchaseRequisitions] failed to record ACH payable', err);
    });
  }

  const issuedLabel = purchaseOrderNumbers({ poNumber, lineItems: issuedLines }).join(', ');
  const summary = describePurchase({
    amount: requisition.amount,
    title: requisition.title,
    vendorName: issuedHeaderVendor || requisition.vendorName,
    costLabel: requisition.costObject.label,
  });

  if (isFinal) {
    await notifyPurchaseRequisition({
      userId: requisition.requesterId,
      title: `Purchase approved — ${issuedLabel || requisition.prNumber}`,
      message: `${summary}. Pay by ${paymentMethod}${
        params.paymentMethodNote?.trim() || requisition.paymentMethodNote
          ? ` (${(params.paymentMethodNote || requisition.paymentMethodNote || '').trim()})`
          : ''
      }.`,
      prId: requisition.id,
      skipUserId: params.actorId,
    });
  } else if (nextPending) {
    await notifyPurchaseRequisition({
      userId: nextPending.userId,
      title: 'Purchase requisition needs your approval',
      message: `${requisition.requesterName} · ${summary}`,
      prId: requisition.id,
      skipUserId: params.actorId,
    });
  }
}

export async function returnPurchaseRequisition(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName?: string;
  note: string;
  reject?: boolean;
  override?: boolean;
}): Promise<void> {
  const note = params.note.trim();
  if (!note) throw new Error('Add a note so the requester knows what to change.');
  const requesterId = params.requisition.requesterId?.trim();
  if (!requesterId) throw new Error('This purchase has no requester to return it to.');
  const idx = currentPendingStepIndex(params.requisition.approvalSteps);
  const issuedAgentReturn =
    idx < 0 &&
    params.requisition.createdViaAgent === true &&
    (params.requisition.alreadyPurchased === true ||
      params.requisition.status === 'approved' ||
      params.requisition.status === 'purchased');
  if (idx < 0 && !issuedAgentReturn) throw new Error('This requisition is not awaiting approval.');
  if (issuedAgentReturn && requesterId === params.actorId) {
    throw new Error('This purchase is already with the requester.');
  }
  if (
    issuedAgentReturn &&
    (params.requisition.qbBillId || params.requisition.purchasedVia === 'supplier_bill')
  ) {
    throw new Error('This purchase is already on a bill and cannot be returned.');
  }
  const step = idx >= 0 ? params.requisition.approvalSteps[idx] : undefined;
  if (step && !params.override && step.userId !== params.actorId) {
    throw new Error('This requisition is waiting on someone else.');
  }
  const now = new Date();
  const status: PurchaseRequisitionStatus = params.reject ? 'rejected' : 'returned';
  const stepStatus: ApprovalStepStatus = params.reject ? 'rejected' : 'returned';
  const substituteActor = !!params.override && !!step && step.userId !== params.actorId;
  const actorName = params.actorName?.trim() || 'Staff';
  const nextSteps: PurchaseApprovalStep[] = step
    ? params.requisition.approvalSteps.map((s, i) =>
        i === idx
          ? finalizePurchaseApprovalStep(
              s,
              stepStatus,
              { id: params.actorId, name: actorName },
              { note, decidedAt: now, substituteActor },
            )
          : s,
      )
    : [
        ...params.requisition.approvalSteps,
        {
          gate: 'executive',
          roleLabel: 'Approver',
          userId: params.actorId,
          userName: actorName,
          status: stepStatus,
          decidedAt: now,
          note,
        },
      ];
  // CRM #73: withdraw issued numbers for any approved PO (CRM or agent), and for
  // agent already-purchased rows. Always clear sticky per-line stamps too.
  const withdrawIssued =
    !params.reject &&
    (params.requisition.status === 'approved' ||
      (params.requisition.createdViaAgent === true &&
        params.requisition.alreadyPurchased === true &&
        params.requisition.status === 'purchased'));
  const docLabel =
    purchaseOrderNumbers(params.requisition).join(', ') ||
    params.requisition.poNumber ||
    params.requisition.prNumber;
  const clearedLines = withdrawIssued
    ? serializePurchaseLineItems(
        clearLinePoNumbers(params.requisition.lineItems),
        params.requisition.salesTaxPct ?? 0,
      )
    : null;
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, params.requisition.id), {
    approvalSteps: serializeSteps(nextSteps),
    approvalStepUserIds: nextSteps.map(s => s.userId),
    currentStepIndex: idx >= 0 ? idx : Math.max(0, nextSteps.length - 1),
    status,
    currentApproverId: null,
    hold: deleteField(),
    ...(withdrawIssued
      ? {
          poNumber: deleteField(),
          alreadyPurchased: deleteField(),
          purchasedAt: deleteField(),
          purchasedAmount: deleteField(),
          purchasedById: deleteField(),
          purchasedByName: deleteField(),
          purchasedVia: deleteField(),
          approvedAt: deleteField(),
          approvedAmount: deleteField(),
          paymentMethod: deleteField(),
          paymentMethodId: deleteField(),
          paymentMethodNote: deleteField(),
          ...(clearedLines ? { lineItems: clearedLines } : {}),
        }
      : {}),
    updatedAt: serverTimestamp(),
  });
  await notifyPurchaseRequisition({
    userId: requesterId,
    title: params.reject ? 'Purchase requisition rejected' : 'Purchase returned for your review',
    message: params.reject ? note : `${actorName} returned ${docLabel} for your review. ${note}`,
    prId: params.requisition.id,
    skipUserId: params.actorId,
  });
}

export async function cancelPurchaseRequisition(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  access: PurchasingActorAccess;
}): Promise<void> {
  const { requisition } = params;
  if (!canCancelPurchaseRequisition(params.access, requisition, params.actorId)) {
    throw new Error('You cannot cancel this purchase.');
  }
  const issued = !!requisition.poNumber || requisition.status === 'approved' || requisition.status === 'purchased';
  const label = requisition.poNumber || requisition.prNumber;
  const current = currentPendingStep(requisition.approvalSteps);
  const now = new Date();
  const nextSteps: PurchaseApprovalStep[] = requisition.approvalSteps.map(s =>
    s.status === 'pending'
      ? finalizePurchaseApprovalStep(
          s,
          'skipped',
          { id: params.actorId, name: params.actorName },
          { note: `Cancelled by ${params.actorName}`, decidedAt: now },
        )
      : s,
  );
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id), {
    approvalSteps: serializeSteps(nextSteps),
    status: 'cancelled',
    currentApproverId: null,
    hold: deleteField(),
    cancelledAt: serverTimestamp(),
    cancelledById: params.actorId,
    cancelledByName: params.actorName,
    updatedAt: serverTimestamp(),
  });
  await deleteDoc(internalReviewRef(requisition.id)).catch(() => undefined);
  const title = issued ? 'Purchase order cancelled' : 'Purchase requisition cancelled';
  const message = `${label} · ${requisition.title}`;
  await notifyPurchaseRequisition({
    userId: requisition.requesterId,
    title,
    message,
    prId: requisition.id,
    skipUserId: params.actorId,
  });
  if (current) {
    await notifyPurchaseRequisition({
      userId: current.userId,
      title,
      message,
      prId: requisition.id,
      skipUserId: params.actorId,
    });
  }
}

export async function reassignPurchaseRequester(params: {
  requisition: PurchaseRequisition;
  requesterId: string;
  requesterName: string;
  actorId: string;
  access: PurchasingActorAccess;
}): Promise<void> {
  const requesterId = params.requesterId.trim();
  const requesterName = params.requesterName.trim();
  if (!requesterId || !requesterName) throw new Error('Pick who requested this purchase.');
  if (!canReassignPurchaseRequester(params.access)) {
    throw new Error('You cannot reassign the requester on this purchase.');
  }
  if (
    params.requisition.requesterId === requesterId
    && params.requisition.requesterName === requesterName
  ) {
    return;
  }
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, params.requisition.id), {
    requesterId,
    requesterName,
    updatedAt: serverTimestamp(),
  });
}

async function collectStoragePaths(folder: string, into: Set<string>): Promise<void> {
  try {
    const listed = await listAll(storageRef(storage, folder));
    listed.items.forEach(item => into.add(item.fullPath));
    await Promise.all(listed.prefixes.map(prefix => collectStoragePaths(prefix.fullPath, into)));
  } catch {
    /* Folder may be empty or listing may be denied. */
  }
}

async function deletePurchaseAttachments(prId: string, attachments: PurchaseAttachment[]): Promise<void> {
  const paths = new Set(attachments.map(file => file.storagePath).filter(Boolean));
  await collectStoragePaths(`purchase-requisitions/${prId}`, paths);
  await Promise.all(
    Array.from(paths).map(path => deleteObject(storageRef(storage, path)).catch(() => undefined)),
  );
}

export async function deletePurchaseRequisition(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  access: PurchasingActorAccess;
}): Promise<void> {
  const { requisition } = params;
  if (!canDeletePurchaseRequisition(params.access, requisition, params.actorId)) {
    throw new Error('You cannot delete this purchase.');
  }
  const label = requisition.poNumber || requisition.prNumber;
  await deletePurchaseAttachments(requisition.id, requisition.attachments);
  await deleteDoc(internalReviewRef(requisition.id)).catch(() => undefined);
  await deleteDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id));
  await notifyPurchaseRequisition({
    userId: requisition.requesterId,
    title: requisition.poNumber ? 'Purchase order deleted' : 'Purchase requisition deleted',
    message: `${label} · ${requisition.title}`,
    prId: requisition.id,
    skipUserId: params.actorId,
  });
}

function internalReviewRef(prId: string) {
  return doc(db, PURCHASE_REQUISITIONS_COLLECTION, prId, PURCHASE_INTERNAL_COLLECTION, PURCHASE_INTERNAL_REVIEW_DOC);
}

export function subscribePurchaseInternalReview(
  prId: string,
  onHold: (hold: PurchaseHoldInternal | null) => void,
): Unsubscribe {
  return onSnapshot(
    internalReviewRef(prId),
    snap => {
      const data = snap.data();
      onHold(data ? parseInternalHold(data) || null : null);
    },
    () => onHold(null),
  );
}

export async function holdPurchaseRequisition(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  reasonId: string;
  reasonLabel: string;
  note?: string;
  visibleToRequester: boolean;
}): Promise<void> {
  const { requisition } = params;
  if (!canHoldPurchaseStatus(requisition.status)) {
    throw new Error('This requisition cannot be held.');
  }
  if (requisition.status === 'purchased') throw new Error('A purchased order cannot be held.');
  const note = params.note?.trim() || '';
  const reason = { id: params.reasonId, label: params.reasonLabel, visibleToRequesterDefault: params.visibleToRequester };
  const publicLabel = params.visibleToRequester ? publicHoldLabel(reason, note) : undefined;
  const resumeStatus = requisition.hold?.resumeStatus || requisition.status;
  const issued = requisition.status === 'approved' || !!requisition.poNumber;
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id), {
    hold: {
      visibleToRequester: params.visibleToRequester,
      resumeStatus,
      ...(publicLabel ? { publicLabel } : {}),
    },
    currentApproverId: issued ? params.actorId : requisition.currentApproverId || params.actorId,
    updatedAt: serverTimestamp(),
  });
  await setDoc(internalReviewRef(requisition.id), {
    reasonId: params.reasonId,
    reasonLabel: params.reasonLabel,
    note: note || null,
    visibleToRequester: params.visibleToRequester,
    heldById: params.actorId,
    heldByName: params.actorName,
    heldAt: Timestamp.now(),
  });
  await notifyPurchaseRequisition({
    userId: requisition.requesterId,
    title: requisition.poNumber ? `${requisition.poNumber} is on hold` : 'Purchase request on hold',
    message: requesterHoldMessage({
      visibleToRequester: params.visibleToRequester,
      publicLabel,
      resumeStatus,
    }),
    prId: requisition.id,
    skipUserId: params.actorId,
  });
}

export async function releasePurchaseHold(params: {
  requisition: PurchaseRequisition;
  actorId: string;
}): Promise<void> {
  const { requisition } = params;
  if (!requisition.hold) throw new Error('This requisition is not on hold.');
  const resume = requisition.hold.resumeStatus;
  const pending = currentPendingStep(requisition.approvalSteps);
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id), {
    hold: deleteField(),
    currentApproverId:
      resume === 'approved' || requisition.poNumber ? null : pending?.userId || requisition.currentApproverId || null,
    updatedAt: serverTimestamp(),
  });
  await deleteDoc(internalReviewRef(requisition.id)).catch(() => undefined);
  await notifyPurchaseRequisition({
    userId: requisition.requesterId,
    title: requisition.poNumber ? `${requisition.poNumber} is moving again` : 'Purchase review resumed',
    message: describePurchase({
      amount: requisition.amount,
      title: requisition.title,
      vendorName: requisition.vendorName,
      costLabel: requisition.costObject.label,
    }),
    prId: requisition.id,
    skipUserId: params.actorId,
  });
}

export async function syncPurchaseClosedWithSupplierBills(params: {
  purchaseRequisitionId: string;
  actorId: string;
  actorName: string;
}): Promise<void> {
  const id = params.purchaseRequisitionId.trim();
  if (!id) return;
  const requisition = await getPurchaseRequisition(id);
  if (!requisition?.poNumber) return;
  if (!purchaseFollowsSupplierBill({
    id: requisition.paymentMethodId,
    label: requisition.paymentMethod,
  })) return;

  const billSnap = await getDocs(query(
    collection(db, 'supplierBills'),
    where('purchaseRequisitionId', '==', id),
  ));
  const { mapBill } = await import('./supplierBillsService');
  const bills = billSnap.docs.map(mapBill);
  const closes = supplierBillPaymentClosesPurchase(bills.map((bill) => bill.status));
  const ref = doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id);

  if (closes && requisition.status === 'approved') {
    await updateDoc(ref, {
      status: 'purchased',
      hold: deleteField(),
      purchaseVariance: deleteField(),
      currentApproverId: null,
      purchasedAt: serverTimestamp(),
      purchasedById: params.actorId,
      purchasedByName: params.actorName,
      purchasedAmount: supplierBillsPurchasedAmount(bills),
      purchasedVia: 'supplier_bill',
      updatedAt: serverTimestamp(),
    });
    await deleteDoc(internalReviewRef(requisition.id)).catch(() => undefined);
    await notifyPurchaseRequisition({
      userId: requisition.requesterId,
      title: `${requisition.poNumber} closed`,
      message: 'The final bill payment was marked paid in Billing & Payables.',
      prId: requisition.id,
      skipUserId: params.actorId,
    });
    return;
  }

  if (!closes && requisition.status === 'purchased' && requisition.purchasedVia === 'supplier_bill') {
    await updateDoc(ref, {
      status: 'approved',
      purchasedAt: deleteField(),
      purchasedById: deleteField(),
      purchasedByName: deleteField(),
      purchasedAmount: deleteField(),
      purchasedVia: deleteField(),
      updatedAt: serverTimestamp(),
    });
    await notifyPurchaseRequisition({
      userId: requisition.requesterId,
      title: `${requisition.poNumber} reopened`,
      message: 'A bill payment was unmarked, so this PO is open again until the final payment is paid.',
      prId: requisition.id,
      skipUserId: params.actorId,
    });
  }
}

export async function markPurchaseRequisitionPurchased(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  purchasedAmount: number;
  authorityConfirm?: boolean;
  receiptFiles?: File[];
  /**
   * When Billing & Payables (or designer-invoice) cannot close this Bill Payment PO
   * — e.g. pre-contract / out-of-scope jobs — allow the card-style receipt close.
   */
  allowBillPaymentManualClose?: boolean;
}): Promise<void> {
  const { requisition } = params;
  if (purchaseFollowsSupplierBill({
    id: requisition.paymentMethodId,
    label: requisition.paymentMethod,
  }) && !params.allowBillPaymentManualClose) {
    throw new Error('Bill Payment POs close when the final bill is marked paid in Billing & Payables.');
  }
  if (!canMarkPurchasedStatus(requisition.status) && !(requisition.status === 'approved' && requisition.hold)) {
    throw new Error('Issue a PO before marking it purchased.');
  }
  const issuedLabel = purchaseOrderNumbers(requisition).join(', ');
  if (!issuedLabel) throw new Error('Issue a PO before marking it purchased.');
  const purchasedAmount = roundPurchaseUsd(params.purchasedAmount);
  if (!Number.isFinite(purchasedAmount) || purchasedAmount < 0) {
    throw new Error('Enter the receipt total.');
  }
  if (!purchaseActualWithinTolerance(requisition.amount, purchasedAmount) && !params.authorityConfirm) {
    throw new Error('This receipt is more than 3% off the PO. Send it to your supervisor.');
  }
  const newReceipts = await uploadPurchaseReceipts(requisition.id, params.receiptFiles || []);
  if (!hasPurchaseReceiptProof(requisition.attachments, requisition.links) && newReceipts.length === 0) {
    throw new Error('Upload a receipt photo or PDF.');
  }
  // CRM #80: always sanitize — parseAttachments fills missing optional fields as
  // `undefined`, and Firestore rejects those when we rewrite the attachments array.
  try {
    await updateDoc(
      doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id),
      sanitizePurchaseWrite({
        status: 'purchased',
        hold: deleteField(),
        purchaseVariance: deleteField(),
        currentApproverId: null,
        purchasedAt: serverTimestamp(),
        purchasedById: params.actorId,
        purchasedByName: params.actorName,
        purchasedAmount,
        ...(newReceipts.length
          ? { attachments: [...requisition.attachments, ...newReceipts] }
          : {}),
        updatedAt: serverTimestamp(),
      }),
    );
  } catch (err) {
    throw new Error(purchaseMarkPurchasedErrorMessage(err));
  }
  await deleteDoc(internalReviewRef(requisition.id)).catch(() => undefined);
  await notifyPurchaseRequisition({
    userId: requisition.requesterId,
    title: `${issuedLabel} marked purchased`,
    message: describePurchase({
      amount: purchasedAmount,
      title: requisition.title,
      vendorName: requisition.vendorName,
      costLabel: requisition.costObject.label,
    }),
    prId: requisition.id,
    skipUserId: params.actorId,
  });
  const purchased: PurchaseRequisition = {
    ...requisition,
    status: 'purchased',
    purchasedAmount,
    purchasedAt: new Date(),
    purchasedById: params.actorId,
    purchasedByName: params.actorName,
    ...(newReceipts.length
      ? { attachments: [...requisition.attachments, ...newReceipts] }
      : {}),
  };
  await matchPurchasedPoToPendingCardExpense(purchased).catch(err => {
    console.warn('[purchaseRequisitions] card expense match failed', err);
  });
}

function serializePurchasedSupplierOrder(
  order: PurchasedSupplierOrder,
  purchasedAt: PurchasedSupplierOrder['purchasedAt'] | ReturnType<typeof serverTimestamp> = order.purchasedAt,
) {
  return {
    poNumber: order.poNumber,
    ...(order.vendorName ? { vendorName: order.vendorName } : {}),
    purchasedAmount: order.purchasedAmount,
    purchasedById: order.purchasedById,
    purchasedByName: order.purchasedByName,
    purchasedAt: purchasedAt instanceof Date ? Timestamp.fromDate(purchasedAt) : purchasedAt,
  };
}

/**
 * Mark one supplier PO purchased. The requisition stays issued until every
 * supplier PO on it has been purchased.
 */
export async function markSupplierPurchaseOrderPurchased(params: {
  requisition: PurchaseRequisition;
  poNumber: string;
  actorId: string;
  actorName: string;
  purchasedAmount: number;
  authorityConfirm?: boolean;
  receiptFiles?: File[];
  allowBillPaymentManualClose?: boolean;
}): Promise<void> {
  const { requisition } = params;
  const poNumber = params.poNumber.trim();
  if (purchaseFollowsSupplierBill({
    id: requisition.paymentMethodId,
    label: requisition.paymentMethod,
  }) && !params.allowBillPaymentManualClose) {
    throw new Error('Bill Payment POs close when the final bill is marked paid in Billing & Payables.');
  }
  if (!canMarkPurchasedStatus(requisition.status) && !(requisition.status === 'approved' && requisition.hold)) {
    throw new Error('Issue a PO before marking it purchased.');
  }
  const orders = issuedSupplierPurchaseOrders(requisition);
  const group = orders.find(order => order.poNumber === poNumber);
  if (!group) throw new Error('That purchase order is not on this requisition.');
  if (findPurchasedSupplierOrder(requisition, poNumber)) {
    throw new Error('That purchase order is already marked purchased.');
  }
  const purchasedAmount = roundPurchaseUsd(params.purchasedAmount);
  if (!Number.isFinite(purchasedAmount) || purchasedAmount < 0) {
    throw new Error('Enter the receipt total.');
  }
  if (!purchaseActualWithinTolerance(group.amount, purchasedAmount) && !params.authorityConfirm) {
    throw new Error('This receipt is more than 3% off the PO. Send it to your supervisor.');
  }
  const variancePo = requisition.purchaseVariance?.poNumber;
  const newReceipts = await uploadPurchaseReceipts(requisition.id, params.receiptFiles || []);
  if (!hasPurchaseReceiptProof(requisition.attachments, requisition.links) && newReceipts.length === 0) {
    throw new Error('Upload a receipt photo or PDF.');
  }
  const nextPurchase: PurchasedSupplierOrder = {
    poNumber,
    ...(group.vendorName ? { vendorName: group.vendorName } : {}),
    purchasedAt: new Date(),
    purchasedById: params.actorId,
    purchasedByName: params.actorName,
    purchasedAmount,
  };
  const nextOrders = [
    ...purchasedSupplierOrders(requisition).filter(order => order.poNumber !== poNumber),
    nextPurchase,
  ];
  const allPurchased = everySupplierPoPurchased({ ...requisition, orderPurchases: nextOrders });
  const clearThisVariance = !!variancePo && variancePo === poNumber;
  const purchasedSum = sumPurchasedSupplierOrders(nextOrders);
  try {
    await updateDoc(
      doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id),
      sanitizePurchaseWrite({
        orderPurchases: nextOrders.map(order =>
          serializePurchasedSupplierOrder(
            order,
            order.poNumber === poNumber ? serverTimestamp() : order.purchasedAt,
          ),
        ),
        ...(newReceipts.length ? { attachments: [...requisition.attachments, ...newReceipts] } : {}),
        ...(clearThisVariance || allPurchased ? { purchaseVariance: deleteField() } : {}),
        ...(clearThisVariance ? { currentApproverId: null } : {}),
        ...(allPurchased
          ? {
              status: 'purchased',
              hold: deleteField(),
              purchaseVariance: deleteField(),
              currentApproverId: null,
              purchasedAt: serverTimestamp(),
              purchasedById: params.actorId,
              purchasedByName: params.actorName,
              purchasedAmount: purchasedSum,
            }
          : {}),
        updatedAt: serverTimestamp(),
      }),
    );
  } catch (err) {
    throw new Error(purchaseMarkPurchasedErrorMessage(err));
  }
  if (allPurchased) {
    await deleteDoc(internalReviewRef(requisition.id)).catch(() => undefined);
  }
  const remaining = orders.filter(order => !nextOrders.some(done => done.poNumber === order.poNumber)).length;
  await notifyPurchaseRequisition({
    userId: requisition.requesterId,
    title: `${poNumber} marked purchased`,
    message: allPurchased
      ? describePurchase({
          amount: purchasedSum,
          title: requisition.title,
          vendorName: group.vendorName || requisition.vendorName,
          costLabel: requisition.costObject.label,
        })
      : `${poNumber} is purchased. ${remaining} supplier ${remaining === 1 ? 'order is' : 'orders are'} still open.`,
    prId: requisition.id,
    skipUserId: params.actorId,
  });
  if (allPurchased) {
    const purchased: PurchaseRequisition = {
      ...requisition,
      status: 'purchased',
      orderPurchases: nextOrders,
      purchasedAmount: purchasedSum,
      purchasedAt: new Date(),
      purchasedById: params.actorId,
      purchasedByName: params.actorName,
    };
    await matchPurchasedPoToPendingCardExpense(purchased).catch(err => {
      console.warn('[purchaseRequisitions] card expense match failed', err);
    });
  }
}

async function matchPurchasedPoToPendingCardExpense(requisition: PurchaseRequisition): Promise<void> {
  const userIds = Array.from(
    new Set([requisition.requesterId, requisition.purchasedById].filter((id): id is string => Boolean(id))),
  );
  if (userIds.length === 0) return;

  const snaps = await Promise.all(
    userIds.map(userId =>
      getDocs(
        query(
          collection(db, 'qbExpenses'),
          where('assignedUserId', '==', userId),
          where('categorizationStatus', '==', 'pending'),
        ),
      ),
    ),
  );
  const seen = new Set<string>();
  const expenses = snaps.flatMap(snap =>
    snap.docs
      .filter(d => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
      })
      .map(d => ({ id: d.id, ...d.data() }) as import('../types').QBExpense & { id: string }),
  );
  if (expenses.length === 0) return;

  const { categorizeCardExpenseFromPurchaseOrder } = await import('./cardExpensePurchaseApply');
  for (const expense of expenses) {
    await categorizeCardExpenseFromPurchaseOrder({
      expense,
      requisitions: [requisition],
    });
  }
}

export async function addPurchaseReceipts(params: {
  requisition: PurchaseRequisition;
  files: File[];
}): Promise<void> {
  if (params.requisition.status !== 'approved' && params.requisition.status !== 'purchased') {
    throw new Error('Upload a receipt after the PO is issued.');
  }
  if (!params.files.length) throw new Error('Choose a receipt photo or PDF.');
  const receipts = await uploadPurchaseReceipts(params.requisition.id, params.files);
  await updateDoc(
    doc(db, PURCHASE_REQUISITIONS_COLLECTION, params.requisition.id),
    sanitizePurchaseWrite({
      attachments: [...params.requisition.attachments, ...receipts],
      updatedAt: serverTimestamp(),
    }),
  );
}

export async function addPurchaseSupportingAttachments(params: {
  requisition: PurchaseRequisition;
  files: File[];
  actorId: string;
  access: PurchasingActorAccess;
}): Promise<void> {
  const { requisition } = params;
  if (!canManagePurchaseSupportingAttachments(params.access, requisition, params.actorId)) {
    throw new Error('You cannot add documentation to this purchase.');
  }
  if (!params.files.length) throw new Error('Choose a screenshot or PDF.');
  const uploaded: PurchaseAttachment[] = [];
  for (const file of params.files) {
    uploaded.push(await uploadPurchaseAttachment(requisition.id, file, 'quote'));
  }
  await updateDoc(
    doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id),
    sanitizePurchaseWrite({
      attachments: [...requisition.attachments, ...uploaded],
      updatedAt: serverTimestamp(),
    }),
  );
}

export async function removePurchaseAttachment(params: {
  requisition: PurchaseRequisition;
  storagePath: string;
  actorId: string;
  access: PurchasingActorAccess;
}): Promise<void> {
  const { requisition } = params;
  const storagePath = params.storagePath.trim();
  if (!storagePath) throw new Error('Missing attachment.');
  const target = requisition.attachments.find(file => file.storagePath === storagePath);
  if (!target) throw new Error('That attachment is already gone.');

  const isReceipt = target.kind === 'receipt' || storagePath.includes('/receipts/');
  if (isReceipt) {
    if (!canRemovePurchaseReceipt(params.access, requisition, params.actorId)) {
      throw new Error('You cannot remove this receipt.');
    }
  } else if (!canManagePurchaseSupportingAttachments(params.access, requisition, params.actorId)) {
    throw new Error('You cannot remove this attachment.');
  }

  const next = withoutPurchaseAttachment(requisition.attachments, storagePath);
  await updateDoc(
    doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id),
    sanitizePurchaseWrite({
      attachments: next,
      updatedAt: serverTimestamp(),
    }),
  );
  await deleteObject(storageRef(storage, storagePath)).catch(() => undefined);
}

export async function requestPurchaseActualVariance(params: {
  requisition: PurchaseRequisition;
  actorId: string;
  actorName: string;
  proposedAmount: number;
  reviewer: { userId: string; userName: string };
  receiptFiles?: File[];
  /** Supplier PO when the requisition was issued as more than one purchase order. */
  poNumber?: string;
}): Promise<void> {
  const { requisition, reviewer } = params;
  if (!canMarkPurchasedStatus(requisition.status) && !(requisition.status === 'approved' && requisition.hold)) {
    throw new Error('Issue a PO before recording the receipt.');
  }
  if (!purchaseOrderNumbers(requisition).length) throw new Error('Issue a PO before recording the receipt.');
  const poNumber = params.poNumber?.trim() || '';
  const group = poNumber ? supplierPurchaseOrderByNumber(requisition, poNumber) : undefined;
  if (poNumber && !group) throw new Error('That purchase order is not on this requisition.');
  if (poNumber && findPurchasedSupplierOrder(requisition, poNumber)) {
    throw new Error('That purchase order is already marked purchased.');
  }
  const pendingPo = requisition.purchaseVariance?.poNumber;
  if (requisition.purchaseVariance && pendingPo && pendingPo !== poNumber) {
    throw new Error(`${pendingPo} is already waiting on a supervisor.`);
  }
  const baseline = group ? group.amount : requisition.amount;
  const proposedAmount = roundPurchaseUsd(params.proposedAmount);
  if (!Number.isFinite(proposedAmount) || proposedAmount < 0) {
    throw new Error('Enter the receipt total.');
  }
  if (purchaseActualWithinTolerance(baseline, proposedAmount)) {
    throw new Error('This receipt is within 3% of the PO. Confirm it yourself.');
  }
  if (!reviewer.userId || reviewer.userId === params.actorId) {
    throw new Error('No supervisor is assigned. Ask an admin to confirm this receipt.');
  }
  const newReceipts = await uploadPurchaseReceipts(requisition.id, params.receiptFiles || []);
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id), sanitizePurchaseWrite({
    currentApproverId: reviewer.userId,
    approvalStepUserIds: arrayUnion(reviewer.userId),
    ...(newReceipts.length ? { attachments: [...requisition.attachments, ...newReceipts] } : {}),
    purchaseVariance: {
      proposedAmount,
      requestedById: params.actorId,
      requestedByName: params.actorName,
      requestedAt: serverTimestamp(),
      reviewerId: reviewer.userId,
      reviewerName: reviewer.userName,
      ...(poNumber ? { poNumber } : {}),
    },
    updatedAt: serverTimestamp(),
  }));
  await notifyPurchaseRequisition({
    userId: reviewer.userId,
    title: `${poNumber || requisition.poNumber || requisition.prNumber} receipt needs your OK`,
    message: describePurchase({
      amount: proposedAmount,
      title: requisition.title,
      vendorName: group?.vendorName || requisition.vendorName,
      costLabel: requisition.costObject.label,
    }),
    prId: requisition.id,
    skipUserId: params.actorId,
  });
}

function mergeRequisitions(rows: PurchaseRequisition[][]): PurchaseRequisition[] {
  const map = new Map<string, PurchaseRequisition>();
  for (const list of rows) {
    for (const row of list) map.set(row.id, row);
  }
  const merged: PurchaseRequisition[] = [];
  map.forEach(row => {
    merged.push(row);
  });
  return merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export function subscribePurchaseRequisitions(
  opts: { userId: string; viewAll: boolean },
  onRows: (rows: PurchaseRequisition[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const col = collection(db, PURCHASE_REQUISITIONS_COLLECTION);
  const toRows = (snap: { docs: { id: string; data: () => Record<string, unknown> }[] }) =>
    snap.docs.map(d => parsePurchaseRequisition(d.id, d.data()));

  if (opts.viewAll) {
    return onSnapshot(
      col,
      snap => onRows(toRows(snap)),
      err => {
        console.error('[purchaseRequisitions] listen failed', err);
        onError?.(err);
      },
    );
  }

  let mine: PurchaseRequisition[] = [];
  let assigned: PurchaseRequisition[] = [];
  const emit = () => onRows(mergeRequisitions([mine, assigned]));

  const unsubMine = onSnapshot(
    query(col, where('requesterId', '==', opts.userId)),
    snap => {
      mine = toRows(snap);
      emit();
    },
    err => {
      console.error('[purchaseRequisitions] mine listen failed', err);
      onError?.(err);
    },
  );
  const unsubAssigned = onSnapshot(
    query(col, where('approvalStepUserIds', 'array-contains', opts.userId)),
    snap => {
      assigned = toRows(snap);
      emit();
    },
    err => {
      console.error('[purchaseRequisitions] assigned listen failed', err);
      onError?.(err);
    },
  );

  return () => {
    unsubMine();
    unsubAssigned();
  };
}

export function subscribeJobPurchaseRequisitions(
  onRows: (rows: PurchaseRequisition[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const col = collection(db, PURCHASE_REQUISITIONS_COLLECTION);
  return onSnapshot(
    query(col, where('spendClass', '==', 'job')),
    snap => onRows(snap.docs.map(d => parsePurchaseRequisition(d.id, d.data()))),
    err => {
      console.error('[purchaseRequisitions] job listen failed', err);
      onError?.(err);
    },
  );
}

export function subscribeOverheadPurchaseRequisitions(
  onRows: (rows: PurchaseRequisition[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const col = collection(db, PURCHASE_REQUISITIONS_COLLECTION);
  return onSnapshot(
    query(col, where('spendClass', '==', 'overhead')),
    snap => onRows(snap.docs.map(d => parsePurchaseRequisition(d.id, d.data()))),
    err => {
      console.error('[purchaseRequisitions] overhead listen failed', err);
      onError?.(err);
    },
  );
}

export async function getPurchaseRequisition(id: string): Promise<PurchaseRequisition | null> {
  const snap = await getDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, id));
  if (!snap.exists()) return null;
  return parsePurchaseRequisition(snap.id, snap.data());
}

export async function stampPurchaseQbBillStatus(args: {
  requisitionId: string;
  qbBillStatus: PurchaseQbBillStatus;
  qbBillId?: string | null;
  qbBillUrl?: string | null;
  qbBillMessage?: string | null;
}): Promise<void> {
  const id = args.requisitionId.trim();
  if (!id) return;
  await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, id), {
    qbBillStatus: args.qbBillStatus,
    qbBillId: args.qbBillId?.trim() || null,
    qbBillUrl: args.qbBillUrl?.trim() || null,
    qbBillMessage: args.qbBillMessage?.trim() || null,
    updatedAt: serverTimestamp(),
  });
}

export function subscribePurchaseRequisition(
  id: string,
  onRow: (row: PurchaseRequisition | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, PURCHASE_REQUISITIONS_COLLECTION, id),
    snap => {
      if (!snap.exists()) {
        onRow(null);
        return;
      }
      onRow(parsePurchaseRequisition(snap.id, snap.data()));
    },
    err => {
      console.error('[purchaseRequisitions] doc listen failed', err);
      onError?.(err);
    },
  );
}
